/**
 * Signed Acceleration/Brake Reference Building
 *
 * Builds per-comparison reference envelopes for signed acceleration and braking,
 * used for chart scaling and hover information in lap comparison views.
 *
 * This module learns the physical limits of the vehicle from the selected laps,
 * binning acceleration by speed to account for aerodynamic drag and power limits.
 */

// ============================================================================
// Constants
// ============================================================================

export const SIGNED_ACCEL_SPEED_BIN_KMH = 10
export const SIGNED_BRAKE_G_CAP = 2.0
export const SIGNED_ACCEL_G_CAP = 1.2
export const SIGNED_MIN_G_REFERENCE = 0.1
/** Chart layout: fraction of the metric band above the zero line (accel side). */
export const ACCEL_BAND_FRACTION = 0.6
/** Fills render at this many visible laps or fewer; lines-only above. */
export const SIGNED_ACCEL_FILL_MAX_LAPS = 3

// ============================================================================
// Types
// ============================================================================

export interface SignedAccelSourcePoint {
  speed?: number
  acceleration?: number // signed g
  accelerationSource?:
    | 'calculated-gps'
    | 'accelerometer'
    | 'sensor-fusion'
    | 'combined'
    | 'gps-derived'
    | 'gps-too-sparse'
    | 'gps-too-noisy'
  accelerationSuspect?: boolean
}

export interface SignedAccelLapInput {
  lapId: string
  points: SignedAccelSourcePoint[]
}

export interface SignedAccelReference {
  maxBrakeG: number
  /**
   * Unfloored bin maxima (read via maxAccelGAtSpeed for the floored interpretation).
   */
  maxAccelGBySpeedBin: Map<number, number>
  fallbackMaxAccelG: number
}

export type SignedAccelGateReason =
  | 'no-accelerometer'
  | 'quality-unknown'
  | 'gps-too-sparse'
  | 'gps-too-noisy'

export interface SignedAccelPoint {
  signedPercent: number | null // + = accel, − = brake; null = gap
  signedG: number | null
  gap: boolean
}

export interface SignedAccelLap {
  status: 'shown' | 'gated'
  gateReason?: SignedAccelGateReason
  points: SignedAccelPoint[] // index-aligned with the input lap's points; empty when gated
}

export interface SignedAccelDisplay {
  reference: SignedAccelReference | null
  laps: Map<string, SignedAccelLap>
  gatedLapIds: string[]
}

// ============================================================================
// Reference Building
// ============================================================================

export function buildSignedAccelReference(
  laps: SignedAccelLapInput[]
): SignedAccelReference | null {
  const binMax = new Map<number, number>()
  let maxBrakeG = 0
  let sawUsablePoint = false
  for (const lapInput of laps) {
    for (const p of lapInput.points) {
      if (p.acceleration === undefined || p.speed === undefined) continue
      if (p.accelerationSuspect) continue
      sawUsablePoint = true
      if (p.acceleration < 0) {
        maxBrakeG = Math.max(
          maxBrakeG,
          Math.min(-p.acceleration, SIGNED_BRAKE_G_CAP)
        )
      } else if (p.acceleration > 0) {
        const bin = Math.floor(p.speed / SIGNED_ACCEL_SPEED_BIN_KMH)
        const capped = Math.min(p.acceleration, SIGNED_ACCEL_G_CAP)
        binMax.set(bin, Math.max(binMax.get(bin) ?? 0, capped))
      }
    }
  }
  if (!sawUsablePoint) return null
  const smoothed = smoothBins(binMax)
  const fallbackMaxAccelG = Math.max(
    SIGNED_MIN_G_REFERENCE,
    ...(smoothed.size > 0 ? smoothed.values() : [SIGNED_MIN_G_REFERENCE])
  )
  return {
    maxBrakeG: Math.max(maxBrakeG, SIGNED_MIN_G_REFERENCE),
    maxAccelGBySpeedBin: smoothed,
    fallbackMaxAccelG,
  }
}

/** Interpolate interior bin gaps; extend the edges ±3 bins with edge values (a physically sane envelope for sparse comparisons). */
function smoothBins(binMax: Map<number, number>): Map<number, number> {
  if (binMax.size === 0) return new Map()
  const bins = [...binMax.keys()].sort((a, b) => a - b)
  const lo = bins[0]
  const hi = bins[bins.length - 1]
  const out = new Map<number, number>()
  for (let b = lo; b <= hi; b++) {
    const direct = binMax.get(b)
    if (direct !== undefined) {
      out.set(b, direct)
      continue
    }
    let prev = b - 1
    while (!binMax.has(prev)) prev--
    let next = b + 1
    while (!binMax.has(next)) next++
    const t = (b - prev) / (next - prev)
    out.set(b, binMax.get(prev)! + t * (binMax.get(next)! - binMax.get(prev)!))
  }
  for (let i = 1; i <= 3; i++) {
    out.set(lo - i, out.get(lo)!)
    out.set(hi + i, out.get(hi)!)
  }
  return out
}

export function maxAccelGAtSpeed(
  ref: SignedAccelReference,
  speedKmh: number
): number {
  const bin = Math.floor(speedKmh / SIGNED_ACCEL_SPEED_BIN_KMH)
  const v = ref.maxAccelGBySpeedBin.get(bin) ?? ref.fallbackMaxAccelG
  return Math.max(v, SIGNED_MIN_G_REFERENCE)
}

// ============================================================================
// Gating + Display Building
// ============================================================================

const SENSOR_SOURCES: ReadonlySet<string> = new Set([
  'accelerometer',
  'sensor-fusion',
  'combined',
])

/**
 * Lap-level accuracy gate. A lap qualifies only when its acceleration is
 * sensor-backed (RaceBox/GoPro). GPS-only laps (phone, PZRacing) are gated;
 * laps with no provenance at all are gated as quality-unknown — never
 * silently drawn (additive honesty).
 */
export function lapGateReason(
  points: SignedAccelSourcePoint[]
): SignedAccelGateReason | null {
  let sawSource = false
  let gpsReason: SignedAccelGateReason | null = null
  for (const p of points) {
    if (p.accelerationSource !== undefined) {
      sawSource = true
      if (SENSOR_SOURCES.has(p.accelerationSource)) return null
      if (p.accelerationSource === 'gps-derived') return null
      if (
        p.accelerationSource === 'gps-too-sparse' ||
        p.accelerationSource === 'gps-too-noisy'
      ) {
        gpsReason = p.accelerationSource
      }
    }
  }
  if (gpsReason) return gpsReason
  return sawSource ? 'no-accelerometer' : 'quality-unknown'
}

export function buildSignedAccelDisplay(
  visibleLaps: SignedAccelLapInput[]
): SignedAccelDisplay {
  const laps = new Map<string, SignedAccelLap>()
  const shown: SignedAccelLapInput[] = []
  for (const lapInput of visibleLaps) {
    const gateReason = lapGateReason(lapInput.points)
    if (gateReason) {
      laps.set(lapInput.lapId, { status: 'gated', gateReason, points: [] })
    } else {
      shown.push(lapInput)
    }
  }
  const reference = buildSignedAccelReference(shown)
  for (const lapInput of shown) {
    const points = lapInput.points.map((p): SignedAccelPoint => {
      if (
        reference === null ||
        p.acceleration === undefined ||
        p.speed === undefined ||
        p.accelerationSuspect
      ) {
        return { signedPercent: null, signedG: null, gap: true }
      }
      const g = p.acceleration
      const signedPercent =
        g >= 0
          ? Math.min(100, (g / maxAccelGAtSpeed(reference, p.speed)) * 100)
          : Math.max(-100, (g / reference.maxBrakeG) * 100)
      return { signedPercent, signedG: g, gap: false }
    })
    laps.set(lapInput.lapId, { status: 'shown', points })
  }
  const gatedLapIds = visibleLaps
    .filter((l) => laps.get(l.lapId)?.status === 'gated')
    .map((l) => l.lapId)
  return { reference, laps, gatedLapIds }
}

/**
 * Human-readable notice for gated laps, single-sourced so mobile and web
 * word it identically. 'no-accelerometer' laps are genuinely GPS-only, so
 * the specific wording holds; a 'quality-unknown' lap has no provenance at
 * all (neither confirmed sensor-backed nor confirmed GPS-only), so mixing
 * one in broadens the copy rather than overclaiming "GPS-only" for it.
 */
export function describeGatedLaps(reasons: SignedAccelGateReason[]): string {
  const n = reasons.length
  const noun = n === 1 ? 'lap' : 'laps'
  const distinct = new Set(reasons)
  if (distinct.has('quality-unknown')) {
    return `${n} ${noun} not shown — accelerometer data unavailable or unverified`
  }
  if (distinct.size === 1) {
    switch ([...distinct][0]) {
      case 'no-accelerometer':
        return `${n} ${noun} not shown — GPS-only, no accelerometer`
      case 'gps-too-sparse':
        return `${n} ${noun} not shown — GPS update rate too low for accel`
      case 'gps-too-noisy':
        return `${n} ${noun} not shown — GPS too noisy for accel`
      // No default case: intentional fall-through — any reason not listed
      // above (mixed reasons, or a future reason added without a case here)
      // lands on the combined-GPS copy below.
    }
  }
  return `${n} ${noun} not shown — GPS-only, accuracy insufficient for accel`
}
